import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { createPortal } from 'react-dom'

const Modal = ({ body, dismissable = true, footer, initialOpen, onClose, size = 'medium', title, trigger }) => {
  const [open, setOpen] = useState(initialOpen || false)

  const closeModal = () => {
    setOpen(false)
    onClose?.()
  }

  const toggleModal = () => {
    setOpen((prevOpen) => {
      if (prevOpen) onClose?.()
      return !prevOpen
    })
  }

  const handleBgClick = (e) => {
    if (!dismissable) return
    if (open && e.target === e.currentTarget) {
      closeModal()
    }
  }

  const handleKeyDown = (e) => {
    if (!dismissable) return
    if (open && e.key === 'Escape') {
      closeModal()
    }
  }

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open])

  const sizeClasses = {
    small: 'w-96',
    medium: 'w-[512px]',
    large: 'w-[768px]',
  }

  return (
    <>
      {trigger && trigger(toggleModal)}

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[1072] flex flex-col items-center justify-center bg-black/60 p-4 overflow-hidden"
            onClick={handleBgClick}
            role="button"
            tabIndex={-1}
          >
            <div className={classNames(
              'flex m-auto max-h-[90vh] max-w-full relative animate-[slideInTop_0.4s_ease-out]',
              sizeClasses[size] || sizeClasses.medium
            )}>
              <div className="bg-white flex flex-col max-h-full overflow-hidden relative w-full rounded-lg">
                <div className="mt-6 mx-6 min-h-8 relative flex-shrink-0 flex justify-center items-center">
                  <h2 className="text-2xl font-bold mb-0 select-none text-center">{title}</h2>

                  {dismissable && (
                    <button
                      type="button"
                      className="text-2xl font-semibold leading-none overflow-hidden absolute right-0 top-0 transition-colors duration-200 select-none hover:text-success focus:text-success"
                      onClick={closeModal}
                    >
                      &times;
                    </button>
                  )}
                </div>

                <div className="flex-auto my-6 overflow-y-auto relative px-6 min-h-0 flex flex-col items-center">
                  {body && typeof body === 'function' ? body(toggleModal) : body}
                </div>

                {footer && (
                  <div className="mb-6 mx-6 flex justify-center flex-shrink-0">
                    {footer(toggleModal)}
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.getElementById('modal')
        )}
    </>
  )
}

Modal.propTypes = {
  body: PropTypes.func,
  dismissable: PropTypes.bool,
  footer: PropTypes.func,
  initialOpen: PropTypes.bool,
  onClose: PropTypes.func,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  title: PropTypes.string.isRequired,
  trigger: PropTypes.func,
}

export default Modal
